import { Link } from '@tanstack/react-router';
import createDebug from 'debug';
import { Archive, ChevronLeft, Inbox, Loader2, Share2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { SharedLinkInfo } from '@/api/sharing';
import { getSharedWithMe } from '@/api/sharing';
import { LinkShareBadge } from '@/components/LinkShareBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const debug = createDebug('app:shared-with-me');

export function SharedWithMeView() {
	const [links, setLinks] = useState<SharedLinkInfo[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [offset, setOffset] = useState(0);
	const limit = 50;

	const loadLinks = useCallback(async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await getSharedWithMe({ limit, offset });
			setLinks(response.links);
			setTotal(response.total);
			debug('Loaded %d shared links (total: %d)', response.links.length, response.total);
		} catch (err) {
			debug('Failed to load shared links: %o', err);
			setError(err instanceof Error ? err.message : 'Failed to load shared links');
		} finally {
			setLoading(false);
		}
	}, [offset]);

	useEffect(() => {
		loadLinks();
	}, [loadLinks]);

	const handleNextPage = useCallback(() => {
		if (offset + limit < total) {
			setOffset((prev) => prev + limit);
		}
	}, [offset, limit, total]);

	const handlePrevPage = useCallback(() => {
		if (offset > 0) {
			setOffset((prev) => Math.max(0, prev - limit));
		}
	}, [offset, limit]);

	if (loading && links.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen">
				<Loader2 className="size-8 animate-spin text-primary mb-4" />
				<p className="text-muted-foreground">Loading shared links...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen">
				<div className="rounded-md bg-destructive/10 border border-destructive/20 p-6 max-w-md">
					<h2 className="text-lg font-semibold text-destructive mb-2">Error Loading Links</h2>
					<p className="text-sm text-destructive/80">{error}</p>
					<Button onClick={loadLinks} className="mt-4">
						Try Again
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="container max-w-5xl mx-auto p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<Link to="/">
						<Button variant="ghost" size="icon">
							<ChevronLeft className="size-5" />
						</Button>
					</Link>
					<div>
						<h1 className="text-2xl font-bold flex items-center gap-2">
							<Share2 className="size-6" />
							Shared with Me
						</h1>
						<p className="text-sm text-muted-foreground mt-1">
							{total} {total === 1 ? 'link' : 'links'} shared with you
						</p>
					</div>
				</div>
			</div>

			{/* Links List */}
			{links.length === 0 ? (
				<div className="flex flex-col items-center justify-center py-20 text-center">
					<Share2 className="size-12 text-muted-foreground/50 mb-4" />
					<h2 className="text-xl font-semibold mb-2">No Shared Links</h2>
					<p className="text-muted-foreground max-w-md">
						When other users share links with you, they will appear here.
					</p>
				</div>
			) : (
				<div className="space-y-3">
					{links.map((link) => (
						<div
							key={link.id}
							className="rounded-lg border bg-card p-4 hover:bg-accent/50 transition-colors"
						>
							<div className="flex items-start justify-between gap-4">
								<div className="flex-1 min-w-0 space-y-2">
									{/* Title */}
									<h3 className="font-medium line-clamp-2">{link.title}</h3>

									{/* URL */}
									<a
										href={link.url}
										target="_blank"
										rel="noopener noreferrer"
										className="text-sm text-primary hover:underline block truncate"
									>
										{link.url}
									</a>

									{/* Description */}
									{link.description && (
										<p className="text-sm text-muted-foreground line-clamp-2">{link.description}</p>
									)}

									{/* Metadata badges */}
									<div className="flex flex-wrap items-center gap-2">
										{/* Shared by badge */}
										<LinkShareBadge
											sharedBy={{
												username: link.shared_by_username,
												display_name: link.shared_by_display_name,
											}}
											role={link.role}
										/>

										{/* Role badge */}
										<Badge
											variant={link.role === 'editor' ? 'default' : 'secondary'}
											className="capitalize"
										>
											{link.role}
										</Badge>

										{/* Archived badge */}
										{link.archivedAt && (
											<Badge variant="outline">
												<Archive className="size-3 mr-1" />
												Archived
											</Badge>
										)}

										{/* Queue badge */}
										{link.queueId === 0 ? (
											<Badge variant="outline">
												<Inbox className="size-3 mr-1" />
												Incoming
											</Badge>
										) : (
											<Badge variant="outline">Queue #{link.queueId}</Badge>
										)}

										{/* Tags */}
										{link.tags && link.tags.length > 0 && (
											<div className="flex flex-wrap items-center gap-1">
												{link.tags.slice(0, 3).map((tag) => (
													<Badge
														key={tag.id}
														variant="outline"
														className="text-xs"
														style={{
															borderColor: tag.color ?? undefined,
															color: tag.color ?? undefined,
														}}
													>
														{tag.icon && <span className="mr-1">{tag.icon}</span>}
														{tag.name}
													</Badge>
												))}
												{link.tags.length > 3 && (
													<span className="text-xs text-muted-foreground">
														+{link.tags.length - 3} more
													</span>
												)}
											</div>
										)}
									</div>
								</div>

								{/* Thumbnail */}
								{link.thumbnailUrl && (
									<div className="shrink-0">
										<img
											src={`/api/thumbnail/${link.id}`}
											alt=""
											className="size-24 rounded-md object-cover bg-muted"
											loading="lazy"
											onError={(e) => {
												(e.target as HTMLImageElement).style.display = 'none';
											}}
										/>
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{/* Pagination */}
			{total > limit && (
				<div className="flex items-center justify-between border-t pt-4">
					<p className="text-sm text-muted-foreground">
						Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
					</p>
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={handlePrevPage}
							disabled={offset === 0 || loading}
						>
							Previous
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={handleNextPage}
							disabled={offset + limit >= total || loading}
						>
							Next
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
